<script setup>
import { ref } from "vue";
import { useRouter } from "vue-router";
import Spinner from "../../components/ui/Spinner.vue";
import EmptyDocumentState from "../../components/ui/EmptyDocumentState.vue";
import { useHome } from "../../composables/useHome.js";
import { useAddModal } from "../../composables/useAddModal.js";
import { formatRelativeDateTime } from "../../composables/useDateFormat.js";
import { permissionsForRole } from "../../composables/usePermissions.js";
import DocumentCardMenu from "../../components/ui/DocumentCardMenu.vue";
import DownloadOnlyModal from "../../components/ui/DownloadOnlyModal.vue";

const router = useRouter();
const { isLoading, activeTab, displayedDocuments, currentUser, timeOfDay, setTab, removeDocument } = useHome();
const { openDocument, openProject } = useAddModal();

const downloadOnlyDoc = ref(null); // set to open DownloadOnlyModal instead of navigating

function openDoc(doc) {
  if (doc.isAnnotatable === false) {
    downloadOnlyDoc.value = doc;
    return;
  }
  router.push({ name: "v2-document", params: { projectId: doc.projectId, docId: doc.id } });
}

// getRecentDocuments/getStaredDocuments now fetch sessionUserRole alongside
// each doc's project, so the menu gates on the real role (e.g. a doc shared
// to us as VIEWER shouldn't see Rename/Delete/Share) instead of assuming OWNER.
function projectFor(doc) {
  return { id: doc.projectId, name: doc.projectName, userRole: doc.sessionUserRole, permissions: permissionsForRole(doc.sessionUserRole) };
}

const formatDate = formatRelativeDateTime;
</script>

<template>
  <div class="flex flex-col p-8 bg-slate-50 dark:bg-slate-900 min-h-full">

    <!-- Greeting -->
    <div class="mb-7">
      <h1 class="text-2xl font-bold text-slate-800 dark:text-slate-100 m-0 mb-1.5">
        Good <span class="text-brand">{{ timeOfDay }}</span>, {{ currentUser.firstName }}
      </h1>
      <p class="text-sm text-slate-500 dark:text-slate-400 m-0">
        Work together on documents. Annotate, share, and collect feedback in real time
      </p>
    </div>

    <!-- Action cards -->
    <div class="flex gap-4 mb-8 flex-wrap">
      <!-- Add document -->
      <button
        class="flex items-center gap-3.5 px-5 py-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl cursor-pointer flex-1 min-w-[240px] max-w-[380px] text-left transition-shadow hover:shadow-md text-slate-400 dark:text-slate-500"
        @click="openDocument"
      >
        <div class="flex items-center justify-center w-11 h-11 rounded-xl bg-green-100 dark:bg-green-950 text-green-600 dark:text-green-400 shrink-0">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" />
          </svg>
        </div>
        <div class="flex flex-col flex-1">
          <span class="text-sm font-semibold text-slate-800 dark:text-slate-100">Add document</span>
          <span class="text-xs text-slate-400 dark:text-slate-500 mt-0.5">PDF, Word, PPTX &amp; 20 more</span>
        </div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="shrink-0">
          <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>

      <!-- Create project -->
      <button
        class="flex items-center gap-3.5 px-5 py-4 bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl cursor-pointer flex-1 min-w-[240px] max-w-[380px] text-left transition-shadow hover:shadow-md text-slate-400 dark:text-slate-500"
        @click="openProject"
      >
        <div class="flex items-center justify-center w-11 h-11 rounded-xl bg-yellow-100 dark:bg-yellow-950 text-yellow-600 dark:text-yellow-400 shrink-0">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
            <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" stroke-width="2" />
            <path d="M16 7V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v2" stroke="currentColor" stroke-width="2" />
          </svg>
        </div>
        <div class="flex flex-col flex-1">
          <span class="text-sm font-semibold text-slate-800 dark:text-slate-100">Create project</span>
          <span class="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Group docs, tasks &amp; files</span>
        </div>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" class="shrink-0">
          <path d="M12 5v14M5 12h14" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>
    </div>

    <!-- Tabs + sort -->
    <div class="flex items-center justify-between mb-1">
      <div class="flex gap-1">
        <button
          :class="['flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-0 bg-transparent cursor-pointer transition-colors border-b-2',
            activeTab === 'recent'
              ? 'text-slate-800 dark:text-slate-100 border-brand font-semibold'
              : 'text-slate-500 dark:text-slate-400 border-transparent hover:text-slate-700 dark:hover:text-slate-300']"
          @click="setTab('recent')"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2" />
            <path d="M12 7v5l3 3" stroke="currentColor" stroke-width="2" stroke-linecap="round" />
          </svg>
          Recent
        </button>
        <button
          :class="['flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-0 bg-transparent cursor-pointer transition-colors border-b-2',
            activeTab === 'starred'
              ? 'text-slate-800 dark:text-slate-100 border-brand font-semibold'
              : 'text-slate-500 dark:text-slate-400 border-transparent hover:text-slate-700 dark:hover:text-slate-300']"
          @click="setTab('starred')"
        >
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
            <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
              stroke="currentColor" stroke-width="2" stroke-linejoin="round" />
          </svg>
          Starred
        </button>
      </div>
      <span class="text-xs text-slate-400 dark:text-slate-500">Sorted by: Last accessed</span>
    </div>

    <!-- Loading -->
    <div v-if="isLoading" class="flex justify-center py-20">
      <Spinner size="48px" />
    </div>

    <!-- Empty state -->
    <EmptyDocumentState v-else-if="displayedDocuments.length === 0" :variant="activeTab" />

    <!-- Document table -->
    <table v-else class="w-full border-collapse bg-white dark:bg-slate-800 rounded-lg overflow-hidden border border-slate-200 dark:border-slate-700">
      <thead>
        <tr class="bg-slate-100 dark:bg-slate-700">
          <th class="w-[40%] px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 text-left border-b border-slate-200 dark:border-slate-700">Document name</th>
          <th class="w-[30%] px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 text-left border-b border-slate-200 dark:border-slate-700">Associated project</th>
          <th class="w-[22%] px-4 py-2.5 text-xs font-semibold text-slate-500 dark:text-slate-400 text-left border-b border-slate-200 dark:border-slate-700">Last accessed</th>
          <th class="w-[8%] border-b border-slate-200 dark:border-slate-700" />
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="doc in displayedDocuments"
          :key="doc.id"
          class="border-b border-slate-100 dark:border-slate-700 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors cursor-pointer"
          @click="openDoc(doc)"
        >
          <td class="px-4 py-3.5 text-sm text-slate-800 dark:text-slate-100">{{ doc.name }}</td>
          <td class="px-4 py-3.5 text-sm text-slate-800 dark:text-slate-100">{{ doc.projectName || "—" }}</td>
          <td class="px-4 py-3.5 text-sm text-slate-500 dark:text-slate-400">{{ formatDate(doc.lastAccessedAt) }}</td>
          <td class="px-4 py-3.5 text-right" @click.stop>
            <DocumentCardMenu :document="doc" :project="projectFor(doc)" :allow-share="doc.isSingleton" @deleted="removeDocument(doc.id)" />
          </td>
        </tr>
      </tbody>
    </table>

    <DownloadOnlyModal
      v-if="downloadOnlyDoc"
      :document="downloadOnlyDoc"
      :project="projectFor(downloadOnlyDoc)"
      @close="downloadOnlyDoc = null"
    />
  </div>
</template>
